import { Loader2, Inbox, Trash2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";

export function PageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <div className="rise flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
      <div className="space-y-1">
        {eyebrow && <div className="label-mono !text-amber-500">{eyebrow}</div>}
        <h1 className="font-display text-3xl md:text-4xl font-extrabold uppercase tracking-tight" data-testid="page-title">{title}</h1>
        {subtitle && <p className="text-sm text-slate-400 max-w-2xl">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

const TONES = {
  amber: "from-amber-500 to-amber-600",
  emerald: "from-emerald-500 to-emerald-600",
  rose: "from-rose-500 to-rose-600",
  blue: "from-blue-500 to-blue-600",
  slate: "from-slate-500 to-slate-600",
};

export function KpiCard({ label, value, sub, tone = "amber", icon: Icon, testid, delay = 0 }) {
  return (
    <div className="rise rounded-lg border border-slate-800 bg-slate-900 p-5 transition-colors hover:border-slate-700" style={{ animationDelay: `${delay}ms` }}>
      <div className="flex items-center justify-between">
        <span className="label-mono">{label}</span>
        {Icon && <Icon className="h-4 w-4 text-slate-500" />}
      </div>
      <div className="mt-3 font-mono text-2xl md:text-[26px] font-bold tracking-tight" data-testid={testid}>{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
      <div className={`mt-4 h-1 w-full rounded-full bg-gradient-to-r ${TONES[tone]}`} />
    </div>
  );
}

export function SectionCard({ title, right, children, className = "", testid }) {
  return (
    <section className={`rise rounded-lg border border-slate-800 bg-slate-900 ${className}`} data-testid={testid}>
      {(title || right) && (
        <div className="flex items-center justify-between gap-3 border-b border-slate-800 px-5 py-3.5">
          <h2 className="font-display text-lg font-bold uppercase tracking-wide">{title}</h2>
          {right}
        </div>
      )}
      {children}
    </section>
  );
}

export function Loader() {
  return (
    <div className="grid place-items-center py-24" data-testid="page-loader">
      <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
    </div>
  );
}

export function EmptyState({ text = "Nothing recorded yet", testid = "empty-state" }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-12 text-slate-500" data-testid={testid}>
      <Inbox className="h-6 w-6" />
      <span className="text-sm">{text}</span>
    </div>
  );
}

export function ConfirmDelete({ title, description, onConfirm, testid }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button data-testid={testid} className="rounded p-1.5 text-slate-500 hover:bg-rose-500/10 hover:text-rose-400 transition-colors">
          <Trash2 className="h-4 w-4" />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testid}-cancel`}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-rose-600 hover:bg-rose-500 text-white" data-testid={`${testid}-confirm`}>
            Confirm
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export const TABLE_HEAD = "bg-slate-950/60 border-b border-slate-800 font-mono text-[11px] uppercase tracking-wider text-slate-400";
export const TABLE_ROW = "border-b border-slate-800/60 hover:bg-slate-800/40 transition-colors";
